
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import Setup from './pages/Setup'
import SetupWizard from './pages/SetupWizard'
import Tickets from './pages/Tickets'
import NewTicket from './pages/NewTicket'
import TicketDetail from './pages/TicketDetail'
import Services from './pages/Services'
import KnowledgeBase from './pages/KnowledgeBase'
import ArticleDetail from './pages/ArticleDetail'
import Profile from './pages/Profile'
import StaffOrganizations from './pages/StaffOrganizations'
import ClientOrganizations from './pages/ClientOrganizations'
import WorkflowConfiguration from './pages/WorkflowConfiguration'
import Analytics from './pages/Analytics'
import ClientDashboard from './pages/ClientDashboard'
import AdvancedTicketCreation from './pages/AdvancedTicketCreation'
import StaffAssignments from './pages/StaffAssignments'
import SmartRouting from './pages/SmartRouting'
import UserManagement from './pages/UserManagement'
import RoleManagement from './pages/RoleManagement'
import AdminWizard from './pages/AdminWizard'
import StaffDashboard from './pages/StaffDashboard'
import ManagerDashboard from './pages/ManagerDashboard'
import PlatformDashboard from './pages/PlatformDashboard'
import ServiceManagement from './pages/ServiceManagement'
import Calendar from './pages/Calendar'
import EmailMonitoring from './pages/EmailMonitoring'
import ChartOfAccounts from './pages/ERP/ChartOfAccounts'
import JournalEntries from './pages/ERP/JournalEntries'
import RecurringJournalEntries from './pages/ERP/RecurringJournalEntries'
import Invoices from './pages/ERP/Invoices'
import Expenses from './pages/ERP/Expenses'
import Payments from './pages/ERP/Payments'
import FinancialReports from './pages/ERP/FinancialReports'
import MultiCurrencyReports from './pages/ERP/MultiCurrencyReports'
import TaxRates from './pages/ERP/TaxRates'
import CostCenters from './pages/ERP/CostCenters'
import AccountingPeriods from './pages/ERP/AccountingPeriods'
import BankReconciliation from './pages/ERP/BankReconciliation'
import DataImport from './pages/ERP/DataImport'
import PurchaseInvoices from './pages/ERP/PurchaseInvoices'
import ExchangeRates from './pages/ERP/ExchangeRates'
import TaxTemplates from './pages/ERP/TaxTemplates'
import TaxGroups from './pages/ERP/TaxGroups'
import TaxExemptions from './pages/ERP/TaxExemptions'
import PaymentReconciliation from './pages/ERP/PaymentReconciliation'
import RecurringInvoices from './pages/ERP/RecurringInvoices'
import RecurringPurchaseInvoices from './pages/ERP/RecurringPurchaseInvoices'
import ScheduledReports from './pages/ERP/ScheduledReports'
import CustomReportTemplates from './pages/ERP/CustomReportTemplates'
import Budgets from './pages/ERP/Budgets'
import EmailTemplates from './pages/ERP/EmailTemplates'
import PrintFormats from './pages/ERP/PrintFormats'
import DocumentNumberSeries from './pages/ERP/DocumentNumberSeries'
import OpeningBalances from './pages/ERP/OpeningBalances'
import Suppliers from './pages/ERP/Suppliers'
import TaxReports from './pages/ERP/TaxReports'
import ProfitabilityReports from './pages/ERP/ProfitabilityReports'
import FinancialAnalytics from './pages/ERP/FinancialAnalytics'
import PurchaseOrders from './pages/ERP/PurchaseOrders'
import GRNs from './pages/ERP/GRNs'
import Projects from './pages/ERP/Projects'
import Assets from './pages/ERP/Assets'
import Payroll from './pages/ERP/Payroll'
import Subscriptions from './pages/ERP/Subscriptions'
import Commissions from './pages/ERP/Commissions'

import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from './stores/authStore'
import axios from 'axios'

function App() {
  const { isAuthenticated, user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [setupStatus, setSetupStatus] = useState<boolean | null>(null)

  // Check setup status on mount
  useEffect(() => {
    const checkSetup = async () => {
      try {
        console.log('🔍 Checking setup status...', { pathname: location.pathname, isAuthenticated })
        const response = await axios.get('/api/setup/status')
        const isComplete = response.data.isSetupComplete
        console.log('✅ Setup status:', { isComplete, pathname: location.pathname, isAuthenticated })
        setSetupStatus(isComplete)
        
        // If setup is complete
        if (isComplete) {
          // If on /setup, redirect to dashboard or login
          if (location.pathname === '/setup') {
            if (isAuthenticated) {
              console.log('🔄 Redirecting authenticated user from /setup to /')
              navigate('/', { replace: true })
            } else {
              console.log('🔄 Redirecting unauthenticated user from /setup to /login')
              navigate('/login', { replace: true })
            }
          }
          // If on root and not authenticated, redirect to login
          else if (location.pathname === '/' && !isAuthenticated) {
            console.log('🔄 Redirecting unauthenticated user from / to /login')
            navigate('/login', { replace: true })
          }
        }
        
        // If setup is not complete and we're not already on /setup, redirect there
        if (!isComplete && location.pathname !== '/setup' && location.pathname !== '/login') {
          console.log('🔄 Setup not complete, redirecting to /setup')
          navigate('/setup', { replace: true })
        }
      } catch (error) {
        console.error('❌ Setup check error:', error)
        // If we can't check setup status, assume it's not complete
        setSetupStatus(false)
        if (location.pathname !== '/setup' && location.pathname !== '/login') {
          navigate('/setup', { replace: true })
        }
      }
    }
    
    checkSetup()
  }, [navigate, location.pathname, isAuthenticated])

  // Helper function to determine the correct dashboard based on user roles
  const getDashboardComponent = () => {
    if (!user?.roles) return <Dashboard />
    
    const roleNames = user.roles
    
    // Client users get client dashboard
    if (roleNames.includes('client')) {
      return <ClientDashboard />
    }
    
    // Staff users get staff dashboard
    if (roleNames.includes('staff') || roleNames.includes('support_staff')) {
      return <StaffDashboard />
    }
    
    // Manager users get manager dashboard
    if (roleNames.includes('manager')) {
      return <ManagerDashboard />
    }
    
    // Admin users get platform dashboard
    if (roleNames.includes('administrator')) {
      return <PlatformDashboard />
    }
    
    // Default fallback
    return <Dashboard />
  }

  // Show loading state while checking setup status
  if (setupStatus === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If setup is complete and user is on /setup, redirect immediately
  if (setupStatus === true && location.pathname === '/setup') {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    } else {
      navigate('/login', { replace: true })
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting...</p>
        </div>
      </div>
    )
  }

  // If setup is complete and user is not authenticated, ensure we're on login page
  // This handles the case where user might be on root or other routes
  if (setupStatus === true && !isAuthenticated && location.pathname !== '/login') {
    // Redirect will happen via useEffect, but show loading while redirecting
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting...</p>
        </div>
      </div>
    )
  }

  return (
    <>
              <Toaster 
          position="top-center"
          reverseOrder={false}
          gutter={8}
          containerClassName=""
          containerStyle={{
            top: '80px', // Position below header
            zIndex: 1000, // Lower than header z-index
          }}
          toastOptions={{
            duration: 4000,
            dismissible: true,
            style: {
              background: '#363636',
              color: '#fff',
              borderRadius: '8px',
              padding: '12px 16px',
              maxWidth: '400px',
              zIndex: 1000,
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000, // Shorter duration for errors
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
            loading: {
              duration: 0,
            },
          }}
        />
      <Routes>
        <Route 
          path="/setup" 
          element={<Setup />} 
        />
        <Route 
          path="/setup-wizard" 
          element={isAuthenticated ? <SetupWizard /> : <Navigate to="/login" replace />} 
        />
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
        />
        <Route 
          path="/" 
          element={isAuthenticated ? <Layout /> : <Navigate to="/login" replace />}
        >
          <Route index element={getDashboardComponent()} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="client-dashboard" element={<ClientDashboard />} />
          <Route path="staff-dashboard" element={<StaffDashboard />} />
          <Route path="manager-dashboard" element={<ManagerDashboard />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="tickets/new" element={<NewTicket />} />
          <Route path="tickets/advanced" element={<AdvancedTicketCreation />} />
          <Route path="tickets/:id" element={<TicketDetail />} />
          <Route path="services" element={<Services />} />
          <Route path="knowledge" element={<KnowledgeBase />} />
          <Route path="knowledge/:id" element={<ArticleDetail />} />
          <Route path="profile" element={<Profile />} />
          <Route path="staff-organizations" element={<StaffOrganizations />} />
          <Route path="client-organizations" element={<ClientOrganizations />} />
          <Route path="workflow-configuration" element={<WorkflowConfiguration />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="staff-assignments" element={<StaffAssignments />} />
          <Route path="smart-routing" element={<SmartRouting />} />
          <Route path="user-management" element={<UserManagement />} />
          <Route path="role-management" element={<RoleManagement />} />
          <Route path="admin-wizard" element={<AdminWizard />} />
          <Route path="seed-management" element={<AdminWizard />} /> {/* Legacy route, redirects to admin-wizard */}
          <Route path="service-management" element={<ServiceManagement />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="email-monitoring" element={<EmailMonitoring />} />
          {/* ERP Routes */}
          <Route path="erp/chart-of-accounts" element={<ChartOfAccounts />} />
          <Route path="erp/journal-entries" element={<JournalEntries />} />
          <Route path="erp/recurring-journal-entries" element={<RecurringJournalEntries />} />
          <Route path="erp/invoices" element={<Invoices />} />
          <Route path="erp/expenses" element={<Expenses />} />
          <Route path="erp/payments" element={<Payments />} />
          <Route path="erp/reports" element={<FinancialReports />} />
          <Route path="erp/multi-currency-reports" element={<MultiCurrencyReports />} />
          <Route path="erp/tax-rates" element={<TaxRates />} />
          <Route path="erp/cost-centers" element={<CostCenters />} />
          <Route path="erp/accounting-periods" element={<AccountingPeriods />} />
          <Route path="erp/bank-reconciliation" element={<BankReconciliation />} />
          <Route path="erp/data-import" element={<DataImport />} />
          <Route path="erp/purchase-invoices" element={<PurchaseInvoices />} />
          <Route path="erp/exchange-rates" element={<ExchangeRates />} />
          <Route path="erp/tax-templates" element={<TaxTemplates />} />
          <Route path="erp/tax-groups" element={<TaxGroups />} />
          <Route path="erp/tax-exemptions" element={<TaxExemptions />} />
          <Route path="erp/payment-reconciliation" element={<PaymentReconciliation />} />
          <Route path="erp/recurring-invoices" element={<RecurringInvoices />} />
          <Route path="erp/recurring-purchase-invoices" element={<RecurringPurchaseInvoices />} />
          <Route path="erp/scheduled-reports" element={<ScheduledReports />} />
          <Route path="erp/custom-report-templates" element={<CustomReportTemplates />} />
          <Route path="erp/budgets" element={<Budgets />} />
          <Route path="erp/email-templates" element={<EmailTemplates />} />
          <Route path="erp/print-formats" element={<PrintFormats />} />
          <Route path="erp/document-number-series" element={<DocumentNumberSeries />} />
          <Route path="erp/opening-balances" element={<OpeningBalances />} />
          <Route path="erp/suppliers" element={<Suppliers />} />
          <Route path="erp/tax-reports" element={<TaxReports />} />
          <Route path="erp/profitability-reports" element={<ProfitabilityReports />} />
          <Route path="erp/financial-analytics" element={<FinancialAnalytics />} />
          <Route path="erp/purchase-orders" element={<PurchaseOrders />} />
          <Route path="erp/grns" element={<GRNs />} />
          <Route path="erp/payroll" element={<Payroll />} />
          <Route path="erp/subscriptions" element={<Subscriptions />} />
          <Route path="erp/commissions" element={<Commissions />} />
        </Route>
      </Routes>
    </>
  )
}

export default App
